import { createTheme, ThemeProvider } from '@mui/material/styles';
import logger from '@next/core-logger';
import axios from 'axios';
import React from 'react';

const SUPPORTED_FONT_FORMAT = "woff2";
const FALLBACK_FONT_FAMILY = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif";
const THEME_FILE_NAME_PREFIX = "theme-fabric-";
const THEME_FOLDER = "ux-fabric/theme";
const FALLBACK_REALM = "logo";
const DEFAULT_VARIANT = "default";
const DEFAULT_TEXT_DIRECTION = "ltr";

var primary = {
	main: "rgba(142, 68, 173, 1)",
	dark: "rgba(127, 61, 155, 1)",
	light: "rgba(155, 80, 186, 1)",
	contrast: "rgba(255, 255, 255, 1)",
	shades: {
		"4p": "rgba(142, 68, 173, 0.04)",
		"8p": "rgba(142, 68, 173, 0.08)",
		"12p": "rgba(142, 68, 173, 0.12)",
		disabled30p: "rgba(142, 68, 173, 0.3)",
		"50p": "rgba(142, 68, 173, 0.5)"
	}
};
var secondary = {
	main: "rgba(0, 0, 0, 1)",
	dark: "rgba(0, 0, 0, 1)",
	light: "rgba(51, 51, 51, 1)",
	contrast: "rgba(255, 255, 255, 1)",
	shades: {
		"4p": "rgba(0, 0, 0, 0.04)",
		"8p": "rgba(0, 0, 0, 0.08)",
		"12p": "rgba(0, 0, 0, 0.12)",
		disabled30p: "rgba(0, 0, 0, 0.3)",
		"50p": "rgba(0, 0, 0, 0.5)"
	}
};
var text = {
	primary: "rgba(0, 0, 0, 1)",
	secondary: "rgba(97, 97, 97, 1)",
	disabled: "rgba(179, 179, 179, 1)",
	contrast: "rgba(255, 255, 255, 1)"
};
var action = {
	"default": {
		enabled: {
			backgroundColor: "rgba(255, 255, 255, 1)",
			borderColor: "rgba(148, 148, 148, 1)",
			iconColor: "rgba(0, 0, 0, 1)"
		},
		hovered: {
			backgroundColor: "rgba(255, 255, 255, 1)",
			borderColor: "rgba(66, 66, 66, 1)"
		},
		selected: {
			backgroundColor: "rgba(255, 255, 255, 1)",
			borderColor: "rgba(0, 0, 0, 1)"
		},
		disabled: {
			backgroundColor: "rgba(255, 255, 255, 1)",
			borderColor: "rgba(189, 189, 189, 1)",
			iconColor: "rgba(179, 179, 179, 1)"
		},
		error: {
			backgroundColor: "rgba(255, 255, 255, 1)",
			borderColor: "rgba(217, 20, 64, 1)",
			iconColor: "rgba(0, 0, 0, 1)"
		}
	},
	strong: {
		enabled: {
			backgroundColor: "rgba(247, 247, 247, 1)",
			borderColor: "rgba(148, 148, 148, 1)",
			borderBottomColor: "rgba(148, 148, 148, 1)"
		},
		hovered: {
			backgroundColor: "rgba(255, 255, 255, 1)",
			borderColor: "rgba(66, 66, 66, 1)",
			borderBottomColor: "rgba(66, 66, 66, 1)"
		},
		selected: {
			backgroundColor: "rgba(255, 255, 255, 1)",
			borderColor: "rgba(0, 0, 0, 1)",
			borderBottomColor: "rgba(0, 0, 0, 1)"
		},
		disabled: {
			backgroundColor: "rgba(255, 255, 255, 1)",
			borderColor: "rgba(189, 189, 189, 1)",
			borderBottomColor: "rgba(189, 189, 189, 1)"
		},
		error: {
			backgroundColor: "rgba(255, 255, 255, 1)",
			borderColor: "rgba(217, 20, 64, 1)",
			iconColor: "rgba(0, 0, 0, 1)",
			borderBottomColor: "rgba(217, 20, 64, 1)"
		}
	}
};
var grey = {
	"50": "rgba(250, 250, 250, 1)",
	"100": "rgba(245, 245, 245, 1)",
	"200": "rgba(238, 238, 238, 1)",
	"300": "rgba(224, 224, 224, 1)",
	"400": "rgba(189, 189, 189, 1)",
	"500": "rgba(158, 158, 158, 1)",
	"600": "rgba(117, 117, 117, 1)",
	"700": "rgba(97, 97, 97, 1)",
	"800": "rgba(66, 66, 66, 1)",
	"900": "rgba(33, 33, 33, 1)",
	white: "rgba(255, 255, 255, 1)",
	black: "rgba(0, 0, 0, 1)"
};
var background = {
	main: "rgba(247, 247, 247, 1)",
	paper: "rgba(255, 255, 255, 1)"
};
var interactive = {
	focus: {
		"default": {
			outlineColor: "rgba(29, 137, 221, 1)"
		}
	}
};
var brand = {
	clearance: {
		"default": {
			backgroundColor: "rgba(239, 122, 34, 1)",
			contrastTextColor: "rgba(255, 255, 255, 1)",
			textColor: "rgba(239, 122, 34, 1)"
		}
	},
	markdown: {
		"default": {
			backgroundColor: "rgba(45, 5, 150, 1)",
			contrastTextColor: "rgba(255, 255, 255, 1)",
			textColor: "rgba(45, 5, 150, 1)"
		}
	},
	promotion: {
		"default": {
			backgroundColor: "rgba(174, 128, 43, 1)",
			contrastTextColor: "rgba(255, 255, 255, 1)",
			textColor: "rgba(174, 128, 43, 1)"
		}
	},
	sale: {
		"default": {
			backgroundColor: "rgba(208, 26, 66, 1)",
			contrastTextColor: "rgba(255, 255, 255, 1)",
			textColor: "rgba(208, 26, 66, 1)"
		},
		vip: {
			enabled: {
				backgroundColor: "rgba(236, 181, 107, 1)",
				contrastTextColor: "rgba(0, 0, 0, 1)",
				textColor: "rgba(236, 181, 107, 1)"
			},
			disabled: {
				backgroundColor: "rgba(249, 233, 211, 1)",
				contrastTextColor: "rgba(179, 179, 179, 1)",
				textColor: "rgba(249, 233, 211, 1)"
			}
		}
	},
	error: {
		"default": {
			textColor: "rgba(217, 20, 64, 1)"
		},
		stock: {
			textColor: "rgba(217, 20, 64, 1)"
		}
	},
	info: {
		"default": {
			textColor: "rgba(29, 137, 221, 1)"
		},
		stock: {
			textColor: "rgba(0, 0, 0, 1)"
		}
	},
	success: {
		"default": {
			textColor: "rgba(37, 127, 57, 1)"
		},
		stock: {
			textColor: "rgba(37, 127, 57, 1)"
		}
	},
	warning: {
		"default": {
			textColor: "rgba(216, 143, 49, 1)"
		},
		stock: {
			textColor: "rgba(216, 143, 49, 1)"
		}
	}
};
var components = {
	alert: {
		error: {
			"default": {
				backgroundColor: "rgba(253, 243, 245, 1)",
				borderColor: "rgba(217, 20, 64, 1)",
				iconColor: "rgba(0, 0, 0, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			}
		},
		warning: {
			"default": {
				backgroundColor: "rgba(253, 249, 245, 1)",
				borderColor: "rgba(216, 143, 49, 1)",
				iconColor: "rgba(0, 0, 0, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			}
		},
		info: {
			"default": {
				backgroundColor: "rgba(244, 249, 253, 1)",
				borderColor: "rgba(29, 137, 221, 1)",
				iconColor: "rgba(0, 0, 0, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			}
		},
		success: {
			"default": {
				backgroundColor: "rgba(244, 249, 246, 1)",
				borderColor: "rgba(37, 127, 57, 1)",
				iconColor: "rgba(0, 0, 0, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			}
		}
	},
	appBar: {
		globalBar: {
			"default": {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderColor: "rgba(247, 247, 247, 1)",
				iconColor: "rgba(0, 0, 0, 1)",
				textColor: "rgba(0, 0, 0, 1)",
				button: {
					outlined: {
						primary: {
							enabled: {
								borderColor: "rgba(0, 0, 0, 1)"
							}
						}
					}
				},
				divider: {
					"default": {
						borderColor: "rgba(238, 238, 238, 1)"
					}
				}
			}
		},
		brandBar: {
			"default": {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(238, 238, 238, 1)",
				iconColor: "rgba(0, 0, 0, 1)",
				textColor: "rgba(0, 0, 0, 1)",
				tab: {
					"default": {
						active: {
							borderColor: "rgba(0, 0, 0, 1)"
						},
						enabled: {
							borderColor: "rgba(158, 158, 158, 1)"
						}
					}
				},
				button: {
					outlined: {
						primary: {
							enabled: {
								borderColor: "rgba(0, 0, 0, 1)"
							}
						}
					}
				}
			},
			secure: {
				borderColor: "rgba(238, 238, 238, 1)"
			}
		},
		navBar: {
			"default": {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderColor: "rgba(238, 238, 238, 1)",
				iconColor: "rgba(0, 0, 0, 1)",
				gradientLeft: "linear-gradient(to right, rgba(247, 247, 247, 1), rgba(247, 247, 247, 0))",
				gradientRight: "linear-gradient(to right, rgba(247, 247, 247, 0.01), rgba(247, 247, 247, 1))"
			}
		},
		backToNext: {
			"default": {
				backgroundColor: "rgba(0, 0, 0, 1)",
				textColor: "rgba(255, 255, 255, 1)",
				iconColor: "rgba(255, 255, 255, 1)"
			}
		}
	},
	avatar: {
		root: {
			borderColor: "rgba(255, 255, 255, 1)"
		},
		iconAvatar: {
			backgroundColor: "rgba(247, 247, 247, 1)",
			iconColor: "rgba(0, 0, 0, 1)"
		},
		textAvatar: {
			backgroundColor: "rgba(247, 247, 247, 1)",
			textColor: "rgba(0, 0, 0, 1)"
		}
	},
	backdrop: {
		"default": {
			backgroundColor: "rgba(0, 0, 0, 0.8)"
		},
		paper: {
			backgroundColor: "rgba(255, 255, 255, 0.8)"
		}
	},
	badge: {
		primary: {
			backgroundColor: "rgba(142, 68, 173, 1)",
			iconColor: "rgba(255, 255, 255, 1)",
			textColor: "rgba(255, 255, 255, 1)"
		},
		secondary: {
			backgroundColor: "rgba(0, 0, 0, 1)",
			iconColor: "rgba(255, 255, 255, 1)",
			textColor: "rgba(255, 255, 255, 1)"
		},
		notification: {
			backgroundColor: "rgba(232, 16, 127, 1)",
			iconColor: "rgba(255, 255, 255, 1)",
			textColor: "rgba(255, 255, 255, 1)"
		}
	},
	breadcrumb: {
		"default": {
			backgroundColor: "rgba(247, 247, 247, 1)",
			borderBottomColor: "rgba(238, 238, 238, 1)",
			borderTopColor: "rgba(238, 238, 238, 0)",
			button: {
				enabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderTopColor: "rgba(238, 238, 238, 0)",
					borderBottomColor: "rgba(238, 238, 238, 1)",
					borderLeftColor: "rgba(238, 238, 238, 1)",
					borderRightColor: "rgba(238, 238, 238, 1)",
					contrastTextColor: "rgba(0, 0, 0, 1)",
					iconColor: "rgba(0, 0, 0, 1)"
				},
				hovered: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderTopColor: "rgba(238, 238, 238, 1)",
					borderBottomColor: "rgba(238, 238, 238, 1)",
					borderLeftColor: "rgba(238, 238, 238, 1)",
					borderRightColor: "rgba(238, 238, 238, 1)",
					contrastTextColor: "rgba(0, 0, 0, 1)",
					iconColor: "rgba(0, 0, 0, 1)"
				}
			},
			gradientLeft: "linear-gradient(to right, rgba(247, 247, 247, 1), rgba(247, 247, 247, 0))",
			gradientRight: "linear-gradient(to right, rgba(247, 247, 247, 0), rgba(247, 247, 247, 1))"
		}
	},
	button: {
		contained: {
			primary: {
				enabled: {
					contrastTextColor: "rgba(255, 255, 255, 1)",
					iconColor: "rgba(255, 255, 255, 1)",
					backgroundColor: "rgba(142, 68, 173, 1)"
				},
				hovered: {
					backgroundColor: "rgba(127, 61, 155, 1)",
					contrastTextColor: "rgba(255, 255, 255, 1)",
					iconColor: "rgba(255, 255, 255, 1)"
				},
				disabled: {
					backgroundColor: "rgba(213, 195, 224, 1)",
					contrastTextColor: "rgba(255, 255, 255, 1)",
					iconColor: "rgba(255, 255, 255, 1)"
				}
			},
			secondary: {
				enabled: {
					backgroundColor: "rgba(0, 0, 0, 1)",
					contrastTextColor: "rgba(255, 255, 255, 1)",
					iconColor: "rgba(255, 255, 255, 1)"
				},
				hovered: {
					backgroundColor: "rgba(51, 51, 51, 1)",
					contrastTextColor: "rgba(255, 255, 255, 1)",
					iconColor: "rgba(255, 255, 255, 1)"
				},
				disabled: {
					backgroundColor: "rgba(179, 179, 179, 1)",
					contrastTextColor: "rgba(255, 255, 255, 1)",
					iconColor: "rgba(255, 255, 255, 1)"
				}
			}
		},
		outlined: {
			primary: {
				enabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(142, 68, 173, 1)",
					contrastTextColor: "rgba(142, 68, 173, 1)",
					iconColor: "rgba(142, 68, 173, 1)"
				},
				hovered: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(127, 61, 155, 1)",
					contrastTextColor: "rgba(127, 61, 155, 1)",
					iconColor: "rgba(127, 61, 155, 1)"
				},
				disabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(213, 195, 224, 1)",
					contrastTextColor: "rgba(213, 195, 224, 1)",
					iconColor: "rgba(213, 195, 224, 1)"
				}
			},
			secondary: {
				enabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(0, 0, 0, 1)",
					contrastTextColor: "rgba(0, 0, 0, 1)",
					iconColor: "rgba(0, 0, 0, 1)"
				},
				hovered: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(51, 51, 51, 1)",
					contrastTextColor: "rgba(51, 51, 51, 1)",
					iconColor: "rgba(51, 51, 51, 1)"
				},
				disabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(179, 179, 179, 1)",
					contrastTextColor: "rgba(179, 179, 179, 1)",
					iconColor: "rgba(179, 179, 179, 1)"
				}
			}
		},
		unstyled: {
			primary: {
				enabled: {
					contrastTextColor: "rgba(142, 68, 173, 1)",
					iconColor: "rgba(142, 68, 173, 1)"
				},
				hovered: {
					contrastTextColor: "rgba(127, 61, 155, 1)",
					iconColor: "rgba(127, 61, 155, 1)"
				},
				disabled: {
					contrastTextColor: "rgba(213, 195, 224, 1)",
					iconColor: "rgba(213, 195, 224, 1)"
				}
			},
			secondary: {
				enabled: {
					contrastTextColor: "rgba(0, 0, 0, 1)",
					iconColor: "rgba(0, 0, 0, 1)"
				},
				hovered: {
					contrastTextColor: "rgba(51, 51, 51, 1)",
					iconColor: "rgba(51, 51, 51, 1)"
				},
				disabled: {
					contrastTextColor: "rgba(179, 179, 179, 1)",
					iconColor: "rgba(179, 179, 179, 1)"
				}
			}
		}
	},
	buttonGroup: {
		"default": {
			borderBottomColor: "rgba(238, 238, 238, 1)",
			borderTopColor: "rgba(238, 238, 238, 1)",
			borderColor: "rgba(238, 238, 238, 1)"
		}
	},
	card: {
		cardHeader: {
			"default": {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderColor: "rgba(238, 238, 238, 1)",
				textColor: "rgba(0, 0, 0, 1)",
				iconColor: "rgba(0, 0, 0, 1)"
			}
		},
		cardActions: {
			"default": {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderColor: "rgba(238, 238, 238, 1)"
			}
		},
		cardContent: {
			"default": {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(217, 217, 217, 1)"
			}
		}
	},
	checkbox: {
		"default": {
			unselected: {
				enabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(146, 146, 146, 1)"
				},
				hovered: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(0, 0, 0, 1)"
				},
				disabled: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					borderColor: "rgba(189, 189, 189, 1)"
				}
			},
			selected: {
				enabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(142, 68, 173, 1)"
				},
				hovered: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(127, 61, 155, 1)"
				},
				disabled: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					borderColor: "rgba(189, 189, 189, 1)"
				}
			},
			checkmark: {
				enabled: {
					iconColor: "rgba(0, 0, 0, 1)"
				},
				disabled: {
					iconColor: "rgba(189, 189, 189, 1)"
				}
			}
		}
	},
	chip: {
		filled: {
			"default": {
				enabled: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					contrastTextColor: "rgba(0, 0, 0, 1)",
					iconColor: "rgba(0, 0, 0, 1)"
				},
				hovered: {
					backgroundColor: "rgba(234, 234, 234, 1)",
					contrastTextColor: "rgba(0, 0, 0, 1)",
					iconColor: "rgba(0, 0, 0, 1)"
				},
				disabled: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					contrastTextColor: "rgba(179, 179, 179, 1)",
					iconColor: "rgba(179, 179, 179, 1)"
				}
			},
			attribute: {
				enabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					contrastTextColor: "rgba(0, 0, 0, 1)"
				}
			}
		},
		outlined: {
			"default": {
				enabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(148, 148, 148, 1)",
					contrastTextColor: "rgba(0, 0, 0, 1)",
					iconColor: "rgba(0, 0, 0, 1)"
				},
				hovered: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					borderColor: "rgba(66, 66, 66, 1)",
					contrastTextColor: "rgba(0, 0, 0, 1)",
					iconColor: "rgba(0, 0, 0, 1)"
				},
				disabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(179, 179, 179, 1)",
					contrastTextColor: "rgba(179, 179, 179, 1)",
					iconColor: "rgba(179, 179, 179, 1)"
				}
			}
		}
	},
	dialog: {
		dialogHeader: {
			"default": {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderBottomColor: "rgba(238, 238, 238, 0)",
				borderColor: "rgba(238, 238, 238, 1)",
				textColor: "rgba(0, 0, 0, 1)",
				iconColor: "rgba(0, 0, 0, 1)"
			}
		},
		dialogContent: {
			"default": {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(238, 238, 238, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			}
		},
		dialogActions: {
			"default": {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderColor: "rgba(238, 238, 238, 1)",
				borderTopColor: "rgba(238, 238, 238, 0)",
				textColor: "rgba(0, 0, 0, 1)"
			}
		}
	},
	divider: {
		"default": {
			borderColor: "rgba(238, 238, 238, 1)"
		},
		strong: {
			borderColor: "rgba(148, 148, 148, 1)"
		}
	},
	drawer: {
		"default": {
			iconColor: "rgba(255, 255, 255, 1)"
		}
	},
	floatingActionButton: {
		"default": {
			enabled: {
				backgroundColor: "rgba(0, 0, 0, 1)",
				iconColor: "rgba(255, 255, 255, 1)"
			},
			focused: {
				backgroundColor: "rgba(0, 0, 0, 1)",
				iconColor: "rgba(255, 255, 255, 1)"
			},
			hovered: {
				backgroundColor: "rgba(51, 51, 51, 1)",
				iconColor: "rgba(255, 255, 255, 1)"
			}
		}
	},
	footer: {
		"default": {
			backgroundColor: "rgba(247, 247, 247, 1)",
			action: {
				"default": {
					enabled: {
						iconColor: "rgba(0, 0, 0, 1)"
					}
				}
			},
			link: {
				primary: {
					enabled: {
						textColor: "rgba(127, 61, 155, 1)"
					},
					hovered: {
						textColor: "rgba(127, 61, 155, 1)"
					}
				},
				inherit: {
					enabled: {
						textColor: "rgba(0, 0, 0, 1)"
					},
					hovered: {
						textColor: "rgba(0, 0, 0, 1)"
					}
				}
			},
			text: {
				primary: "rgba(0, 0, 0, 1)",
				secondary: "rgba(97, 97, 97, 1)"
			},
			divider: {
				"default": {
					borderColor: "rgba(238, 238, 238, 1)"
				}
			}
		}
	},
	imageCarousel: {
		"default": {
			button: {
				contained: {
					addToBag: {
						enabled: {
							backgroundColor: "rgba(142, 68, 173, 1)",
							textColor: "rgba(255, 255, 255, 1)",
							iconColor: "rgba(255, 255, 255, 1)"
						},
						hovered: {
							backgroundColor: "rgba(127, 61, 155, 1)",
							textColor: "rgba(255, 255, 255, 1)",
							iconColor: "rgba(255, 255, 255, 1)"
						},
						disabled: {
							backgroundColor: "rgba(213, 195, 224, 1)",
							textColor: "rgba(255, 255, 255, 1)",
							iconColor: "rgba(255, 255, 255, 1)"
						}
					}
				},
				outlined: {
					enlarge: {
						enabled: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(148, 148, 148, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						},
						hovered: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(0, 0, 0, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						},
						disabled: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(189, 189, 189, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						}
					},
					favourite: {
						enabled: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(148, 148, 148, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						},
						hovered: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(0, 0, 0, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						},
						disabled: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(189, 189, 189, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						}
					},
					navigation: {
						enabled: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(148, 148, 148, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						},
						hovered: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(0, 0, 0, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						},
						disabled: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(189, 189, 189, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						}
					},
					video: {
						enabled: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(148, 148, 148, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						},
						hovered: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(0, 0, 0, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						},
						disabled: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(189, 189, 189, 1)",
							iconColor: "rgba(0, 0, 0, 1)"
						}
					}
				}
			}
		}
	},
	link: {
		primary: {
			enabled: {
				textColor: "rgba(127, 61, 155, 1)"
			},
			hovered: {
				textColor: "rgba(127, 61, 155, 1)"
			}
		},
		inherit: {
			enabled: {
				textColor: "rgba(0, 0, 0, 1)"
			},
			hovered: {
				textColor: "rgba(0, 0, 0, 1)"
			}
		},
		contrast: {
			enabled: {
				textColor: "rgba(255, 255, 255, 1)"
			},
			hovered: {
				textColor: "rgba(255, 255, 255, 1)"
			}
		}
	},
	menu: {
		"default": {
			root: {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(222, 222, 222, 1)"
			},
			enabled: {
				textColor: "rgba(0, 0, 0, 1)"
			},
			disabled: {
				textColor: "rgba(179, 179, 179, 1)"
			},
			hovered: {
				backgroundColor: "rgba(102, 102, 102, 1)",
				textColor: "rgba(255, 255, 255, 1)"
			},
			selected: {
				backgroundColor: "rgba(102, 102, 102, 1)",
				textColor: "rgba(255, 255, 255, 1)"
			}
		},
		megaMenu: {
			"default": {
				backgroundColor: "rgba(255, 255, 255, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			},
			mission: {
				backgroundColor: "rgba(247, 247, 247, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			},
			gutter: {
				backgroundColor: "rgba(255, 255, 255, 0)"
			},
			navigationList: {
				"default": {
					divider: {
						"default": {
							borderColor: "rgba(238, 238, 238, 1)"
						}
					}
				},
				quickLinks: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					divider: {
						"default": {
							borderColor: "rgba(238, 238, 238, 1)"
						}
					}
				}
			}
		},
		searchMenu: {
			"default": {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(238, 238, 238, 1)"
			},
			enriched: {
				backgroundColor: "rgba(247, 247, 247, 1)"
			}
		}
	},
	pagination: {
		imageCarousel: {
			root: {
				backgroundColor: "rgba(255, 255, 255, 0.4)",
				borderColor: "rgba(158, 158, 158, 1)"
			},
			enabled: {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(158, 158, 158, 1)",
				iconColor: "rgba(0, 0, 0, 1)"
			},
			active: {
				borderColor: "rgba(0, 0, 0, 1)",
				backgroundColor: "rgba(0, 0, 0, 1)",
				iconColor: "rgba(255, 255, 255, 1)"
			}
		},
		thumbnailCarousel: {
			root: {
				backgroundColor: "rgba(255, 255, 255, 0.4)",
				borderColor: "rgba(238, 238, 238, 1)"
			},
			enabled: {
				borderColor: "rgba(0, 0, 0, 0)"
			},
			hovered: {
				borderColor: "rgba(2, 2, 2, 1)"
			},
			active: {
				borderColor: "rgba(0, 0, 0, 1)"
			}
		}
	},
	pill: {
		"default": {
			active: {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(0, 0, 0, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			},
			disabled: {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderColor: "rgba(234, 234, 234, 1)",
				highlightColor1: "rgba(234, 234, 234, 1)",
				highlightColor2: "rgba(234, 234, 234, 1)",
				textColor: "rgba(113, 113, 113, 1)"
			},
			disabledActive: {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderColor: "rgba(0, 0, 0, 1)",
				highlightColor1: "rgba(234, 234, 234, 1)",
				highlightColor2: "rgba(234, 234, 234, 1)",
				textColor: "rgba(113, 113, 113, 1)"
			},
			enabled: {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(148, 148, 148, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			},
			hovered: {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(66, 66, 66, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			}
		},
		image: {
			active: {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(0, 0, 0, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			},
			disabled: {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderColor: "rgba(234, 234, 234, 1)",
				highlightColor1: "rgba(234, 234, 234, 1)",
				highlightColor2: "rgba(234, 234, 234, 1)",
				textColor: "rgba(113, 113, 113, 1)"
			},
			disabledActive: {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderColor: "rgba(0, 0, 0, 1)",
				highlightColor1: "rgba(234, 234, 234, 1)",
				highlightColor2: "rgba(234, 234, 234, 1)",
				textColor: "rgba(113, 113, 113, 1)"
			},
			enabled: {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(148, 148, 148, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			},
			hovered: {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(66, 66, 66, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			}
		}
	},
	progress: {
		circular: {
			"default": {
				highlightColor: "rgba(0, 0, 0, 1)"
			}
		},
		linear: {
			reviews: {
				root: {
					backgroundColor: "rgba(247, 247, 247, 1)"
				},
				enabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					highlightColor: "rgba(142, 68, 173, 1)"
				},
				hovered: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					highlightColor: "rgba(127, 61, 155, 1)"
				},
				disabled: {
					backgroundColor: "rgba(255, 255, 255, 1)"
				}
			}
		}
	},
	radio: {
		"default": {
			unselected: {
				enabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(146, 146, 146, 1)"
				},
				hovered: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(0, 0, 0, 1)"
				},
				disabled: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					borderColor: "rgba(189, 189, 189, 1)"
				}
			},
			selected: {
				enabled: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(142, 68, 173, 1)"
				},
				hovered: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(127, 61, 155, 1)"
				},
				disabled: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					borderColor: "rgba(189, 189, 189, 1)"
				}
			},
			checkmark: {
				enabled: {
					iconColor: "rgba(0, 0, 0, 1)"
				},
				disabled: {
					iconColor: "rgba(189, 189, 189, 1)"
				}
			}
		}
	},
	rating: {
		"default": {
			enabled: {
				backgroundColor: "rgba(0, 0, 0, 1)"
			},
			hovered: {
				backgroundColor: "rgba(66, 66, 66, 1)"
			},
			disabled: {
				backgroundColor: "rgba(224, 224, 224, 1)"
			}
		}
	},
	ribbon: {
		"default": {
			root: {
				button: {
					outlined: {
						navigation: {
							hovered: {
								backgroundColor: "rgba(255, 255, 255, 1)",
								borderColor: "rgba(234, 234, 234, 1)",
								iconColor: "rgba(0, 0, 0, 1)"
							},
							enabled: {
								iconColor: "rgba(0, 0, 0, 1)",
								backgroundColor: "rgba(255, 255, 255, 1)",
								borderColor: "rgba(234, 234, 234, 1)"
							}
						}
					}
				}
			},
			enabled: {
				borderColor: "rgba(234, 234, 234, 1)"
			}
		},
		recentlyViewed: {
			root: {
				borderColor: "rgba(238, 238, 238, 1)",
				chip: {
					outlined: {
						timestamp: {
							enabled: {
								borderColor: "rgba(238, 238, 238, 1)",
								backgroundColor: "rgba(247, 247, 247, 1)",
								textColor: "rgba(0, 0, 0, 1)"
							}
						}
					}
				}
			},
			enabled: {
				borderColor: "rgba(234, 234, 234, 1)",
				chip: {
					outlined: {
						bag: {
							enabled: {
								backgroundColor: "rgba(0, 0, 0, 1)",
								borderColor: "rgba(0, 0, 0, 1)",
								textColor: "rgba(255, 255, 255, 1)"
							}
						}
					}
				}
			}
		}
	},
	searchBanner: {
		"default": {
			banner: {
				root: {
					backgroundColor: "rgba(247, 247, 247, 1)"
				},
				copyText: {
					textColor: "rgba(0, 0, 0, 1)",
					link: {
						primary: {
							enabled: {
								contrastTextColor: "rgba(127, 61, 155, 1)"
							},
							hovered: {
								contrastTextColor: "rgba(127, 61, 155, 1)"
							}
						}
					}
				},
				chips: {
					outlined: {
						enabled: {
							backgroundColor: "rgba(255, 255, 255, 1)",
							borderColor: "rgba(148, 148, 148, 1)",
							contrastTextColor: "rgba(0, 0, 0, 1)"
						},
						hovered: {
							backgroundColor: "rgba(247, 247, 247, 1)",
							borderColor: "rgba(0, 0, 0, 1)",
							contrastTextColor: "rgba(0, 0, 0, 1)"
						}
					}
				},
				imageChips: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					textColor: "rgba(0, 0, 0, 1)",
					enabled: {
						borderColor: "rgba(148, 148, 148, 1)"
					},
					hovered: {
						borderColor: "rgba(66, 66, 66, 1)"
					}
				}
			},
			stripBanner: {
				backgroundColor: "rgba(254, 208, 61, 1)",
				borderColor: "rgba(224, 224, 224, 0)",
				button: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(0, 0, 0, 1)",
					contrastTextColor: "rgba(0, 0, 0, 1)"
				},
				typography: {
					textColor: "rgba(0, 0, 0, 1)"
				}
			}
		}
	},
	skeleton: {
		"default": {
			backgroundColor: "rgba(245, 245, 245, 1)"
		}
	},
	tab: {
		"default": {
			root: {
				borderColor: "rgba(0, 0, 0, 1)"
			},
			enabled: {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderBottomColor: "rgba(0, 0, 0, 1)",
				borderColor: "rgba(158, 158, 158, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			},
			hovered: {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderBottomColor: "rgba(0, 0, 0, 1)",
				borderColor: "rgba(158, 158, 158, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			},
			active: {
				backgroundColor: "rgba(255, 255, 255, 1)",
				borderBottomColor: "rgba(255, 255, 255, 1)",
				borderColor: "rgba(0, 0, 0, 1)",
				highlightColor: "rgba(142, 68, 173, 1)",
				textColor: "rgba(0, 0, 0, 1)"
			},
			disabled: {
				backgroundColor: "rgba(247, 247, 247, 1)",
				borderBottomColor: "rgba(0, 0, 0, 1)",
				borderColor: "rgba(158, 158, 158, 1)",
				highlightColor: "rgba(158, 158, 158, 1)",
				textColor: "rgba(179, 179, 179, 1)"
			}
		},
		nav: {
			"default": {
				enabled: {
					backgroundColor: "rgba(0, 0, 0, 0)",
					textColor: "rgba(0, 0, 0, 1)"
				},
				active: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					textColor: "rgba(0, 0, 0, 1)",
					highlightColor: "rgba(0, 0, 0, 1)"
				}
			},
			sale: {
				enabled: {
					textColor: "rgba(208, 26, 66, 1)"
				},
				active: {
					textColor: "rgba(208, 26, 66, 1)"
				}
			},
			clearance: {
				enabled: {
					textColor: "rgba(239, 122, 34, 1)"
				},
				active: {
					textColor: "rgba(239, 122, 34, 1)"
				}
			}
		}
	},
	table: {
		"default": {
			enabled: {
				root: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(234, 234, 234, 1)"
				},
				cellHeader: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					borderColor: "rgba(234, 234, 234, 1)",
					borderBottomColor: "rgba(158, 158, 158, 1)",
					textColor: "rgba(0, 0, 0, 1)"
				},
				cellBodyOdd: {
					backgroundColor: "rgba(255, 255, 255, 1)",
					borderColor: "rgba(234, 234, 234, 1)",
					borderBottomColor: "rgba(234, 234, 234, 1)",
					textColor: "rgba(0, 0, 0, 1)"
				},
				cellBodyEven: {
					backgroundColor: "rgba(247, 247, 247, 1)",
					borderColor: "rgba(234, 234, 234, 1)",
					borderBottomColor: "rgba(234, 234, 234, 1)",
					textColor: "rgba(0, 0, 0, 1)"
				}
			}
		}
	},
	toggleButton: {
		selected: {
			"default": {
				backgroundColor: "rgba(246, 240, 248, 1)",
				borderColor: "rgba(142, 68, 173, 1)"
			}
		}
	},
	tooltip: {
		"default": {
			backgroundColor: "rgba(255, 255, 255, 1)",
			borderColor: "rgba(238, 238, 238, 1)",
			textColor: "rgba(0, 0, 0, 1)"
		}
	}
};
var global = {
	h1: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "1.125rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	h2: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "1rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	h3: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.9375rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	h4: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	h5: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.8125rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	h6: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	body1: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	body2: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	body3: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	body4: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	body5: {
		fontFamily: "poppins-600",
		fontWeight: 600,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	body6: {
		fontFamily: "poppins-600",
		fontWeight: 600,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	subtitle1: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "1rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	subtitle2: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	overline: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "uppercase",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 1,
		lineHeight: 1.5
	},
	caption: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 0,
		lineHeight: 1.5
	}
};
var component = {
	buttonLarge: {
		fontFamily: "poppins-600",
		fontWeight: 600,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	buttonMedium: {
		fontFamily: "poppins-600",
		fontWeight: 600,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	buttonSmall: {
		fontFamily: "poppins-600",
		fontWeight: 600,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	inputLabel1: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	inputLabel2: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	inputText: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	menuItem: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	menuItemDense: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	pillLabel1: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	pillLabel2: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	tabText1: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	tabText2: {
		fontFamily: "poppins-600",
		fontWeight: 600,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	tabTextNav: {
		fontFamily: "poppins-600",
		fontWeight: 600,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	chip: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.8125rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	chipLabel2: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.6875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	helperText: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	badgeLabel: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.625rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	tooltip: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.625rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	listItemSubtitle1: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	listItemSubtitle2: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.875rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	listItemDescription: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 0,
		lineHeight: 1.5
	},
	avatarInitials: {
		fontFamily: "poppins-400",
		fontWeight: 400,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "1.25rem",
		letterSpacing: 0,
		lineHeight: 1
	},
	searchListItemLabel: {
		fontFamily: "poppins-500",
		fontWeight: 500,
		textTransform: "none",
		textDecoration: "none",
		fontSize: "0.75rem",
		letterSpacing: 0,
		lineHeight: 1.5
	}
};
var elevation = {
	header: "0rem 0.3125rem 0.3125rem -0.1875rem rgba(0, 0, 0, 0), 0rem 0.5rem 0.625rem 0.0625rem rgba(0, 0, 0, 0), 0rem 0.1875rem 0.875rem 0.125rem rgba(0, 0, 0, 0)",
	popover: "0rem 0.3125rem 0.3125rem -0.1875rem rgba(0, 0, 0, 0.2), 0rem 0.5rem 0.625rem 0.0625rem rgba(0, 0, 0, 0.14), 0rem 0.1875rem 0.875rem 0.125rem rgba(0, 0, 0, 0.12)",
	floatingActionButton: "0rem 0.3125rem 0.3125rem -0.1875rem rgba(0, 0, 0, 0.2), 0rem 0.5rem 0.625rem 0.0625rem rgba(0, 0, 0, 0.14), 0rem 0.1875rem 0.875rem 0.125rem rgba(0, 0, 0, 0.12)",
	menu: "0rem 0.3125rem 0.3125rem -0.1875rem rgba(0, 0, 0, 0.2), 0rem 0.5rem 0.625rem 0.0625rem rgba(0, 0, 0, 0.14), 0rem 0.1875rem 0.875rem 0.125rem rgba(0, 0, 0, 0.12)",
	tooltip: "0rem 0.3125rem 0.3125rem -0.1875rem rgba(0, 0, 0, 0.2), 0rem 0.5rem 0.625rem 0.0625rem rgba(0, 0, 0, 0.14), 0rem 0.1875rem 0.875rem 0.125rem rgba(0, 0, 0, 0.12)"
};
var shape = {
	global: {
		button: {
			small: {
				borderRadius: "14px"
			},
			medium: {
				borderRadius: "18px"
			},
			large: {
				borderRadius: "22px"
			}
		},
		iconButton: {
			medium: {
				borderRadius: "18px"
			},
			large: {
				borderRadius: "22px"
			}
		},
		select: {
			medium: {
				borderRadius: "18px"
			},
			large: {
				borderRadius: "22px"
			}
		},
		menu: {
			"default": {
				borderRadius: "8px"
			}
		},
		textField: {
			medium: {
				borderRadius: "18px"
			},
			large: {
				borderRadius: "22px"
			}
		},
		toggleButton: {
			medium: {
				borderRadius: "18px"
			},
			large: {
				borderRadius: "22px"
			}
		},
		fitPill: {
			medium: {
				borderRadius: "18px"
			},
			large: {
				borderRadius: "22px"
			}
		},
		colorPill: {
			medium: {
				borderRadius: "18px"
			},
			large: {
				borderRadius: "22px"
			}
		},
		sizePill: {
			medium: {
				borderRadius: "18px"
			},
			large: {
				borderRadius: "22px"
			}
		},
		floatingActionButton: {
			"default": {
				borderRadius: "22px"
			}
		},
		tooltip: {
			"default": {
				borderRadius: "4px"
			}
		},
		popover: {
			"default": {
				borderRadius: "8px"
			}
		},
		check: {
			"default": {
				borderRadius: "4px"
			}
		},
		chip: {
			small: {
				borderRadius: "12px"
			},
			medium: {
				borderRadius: "16px"
			}
		},
		imageChip: {
			"default": {
				borderRadius: "100px"
			}
		},
		ribbonButton: {
			"default": {
				borderRadius: "4px"
			}
		}
	}
};
var logoRealmTheme = {
	primary: primary,
	secondary: secondary,
	text: text,
	action: action,
	grey: grey,
	background: background,
	interactive: interactive,
	brand: brand,
	components: components,
	global: global,
	component: component,
	elevation: elevation,
	shape: shape
};

const mockUxFabric = Object.assign({}, logoRealmTheme);
const mockTheme = createTheme({ uxfabric: mockUxFabric, direction: "ltr" });

const breakpoints = {
    values: {
        xs: 0,
        sm: 400,
        md: 768,
        lg: 1024,
        xl: 1280,
        xxl: 1440,
    },
};

const typography = (theme) => {
    return {
        fontFamily: `${theme.global.body1.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
        h1: {
            fontFamily: `${theme.global.h1.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.h1.fontSize,
            letterSpacing: theme.global.h1.letterSpacing,
            lineHeight: theme.global.h1.lineHeight,
            fontWeight: theme.global.h1.fontWeight,
        },
        h2: {
            fontFamily: `${theme.global.h2.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.h2.fontSize,
            letterSpacing: theme.global.h2.letterSpacing,
            lineHeight: theme.global.h2.lineHeight,
            fontWeight: theme.global.h2.fontWeight,
        },
        h3: {
            fontFamily: `${theme.global.h3.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.h3.fontSize,
            letterSpacing: theme.global.h3.letterSpacing,
            lineHeight: theme.global.h3.lineHeight,
            fontWeight: theme.global.h3.fontWeight,
        },
        h4: {
            fontFamily: `${theme.global.h4.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.h4.fontSize,
            lineHeight: theme.global.h4.lineHeight,
            letterSpacing: theme.global.h4.letterSpacing,
            fontWeight: theme.global.h4.fontWeight,
        },
        h5: {
            fontFamily: `${theme.global.h5.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.h5.fontSize,
            lineHeight: theme.global.h5.lineHeight,
            letterSpacing: theme.global.h5.letterSpacing,
            fontWeight: theme.global.h5.fontWeight,
        },
        h6: {
            fontFamily: `${theme.global.h6.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.h6.fontSize,
            lineHeight: theme.global.h6.lineHeight,
            letterSpacing: theme.global.h6.letterSpacing,
            fontWeight: theme.global.h6.fontWeight,
        },
        body1: {
            fontFamily: `${theme.global.body1.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.body1.fontSize,
            lineHeight: theme.global.body1.lineHeight,
            letterSpacing: theme.global.body1.letterSpacing,
            fontWeight: theme.global.body1.fontWeight,
        },
        body2: {
            fontFamily: `${theme.global.body2.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.body2.fontSize,
            lineHeight: theme.global.body2.lineHeight,
            letterSpacing: theme.global.body2.letterSpacing,
            fontWeight: theme.global.body2.fontWeight,
        },
        body3: {
            fontFamily: `${theme.global.body3.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.body3.fontSize,
            lineHeight: theme.global.body3.lineHeight,
            letterSpacing: theme.global.body3.letterSpacing,
            fontWeight: theme.global.body3.fontWeight,
        },
        body4: {
            fontFamily: `${theme.global.body4.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.body4.fontSize,
            lineHeight: theme.global.body4.lineHeight,
            letterSpacing: theme.global.body4.letterSpacing,
            fontWeight: theme.global.body4.fontWeight,
        },
        body5: {
            fontFamily: `${theme.global.body5.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.body5.fontSize,
            lineHeight: theme.global.body5.lineHeight,
            letterSpacing: theme.global.body5.letterSpacing,
            fontWeight: theme.global.body5.fontWeight,
        },
        body6: {
            fontFamily: `${theme.global.body6.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.body6.fontSize,
            lineHeight: theme.global.body6.lineHeight,
            letterSpacing: theme.global.body6.letterSpacing,
            fontWeight: theme.global.body6.fontWeight,
        },
        subtitle1: {
            fontFamily: `${theme.global.subtitle1.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.subtitle1.fontSize,
            lineHeight: theme.global.subtitle1.lineHeight,
            letterSpacing: theme.global.subtitle1.letterSpacing,
            fontWeight: theme.global.subtitle1.fontWeight,
        },
        subtitle2: {
            fontFamily: `${theme.global.subtitle2.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.subtitle2.fontSize,
            lineHeight: theme.global.subtitle2.lineHeight,
            letterSpacing: theme.global.subtitle2.letterSpacing,
            fontWeight: theme.global.subtitle2.fontWeight,
        },
        overline: {
            fontFamily: `${theme.global.overline.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.overline.fontSize,
            lineHeight: theme.global.overline.lineHeight,
            letterSpacing: theme.global.overline.letterSpacing,
            fontWeight: theme.global.overline.fontWeight,
        },
        caption: {
            fontFamily: `${theme.global.caption.fontFamily}, ${FALLBACK_FONT_FAMILY}`,
            color: theme.text.primary,
            fontSize: theme.global.caption.fontSize,
            lineHeight: theme.global.caption.lineHeight,
            letterSpacing: theme.global.caption.letterSpacing,
            fontWeight: theme.global.caption.fontWeight,
        },
    };
};

const palette = (theme) => {
    return {
        primary: {
            light: theme.primary.light,
            main: theme.primary.main,
            dark: theme.primary.dark,
            contrastText: theme.primary.contrast,
        },
        secondary: {
            light: theme.secondary.light,
            main: theme.secondary.main,
            dark: theme.secondary.dark,
            contrastText: theme.secondary.contrast,
        },
        error: {
            main: theme.brand.error.default.textColor,
        },
        warning: {
            main: theme.brand.warning.default.textColor,
        },
        info: {
            main: theme.brand.info.default.textColor,
        },
        success: {
            main: theme.brand.success.default.textColor,
        },
        grey: Object.assign({}, theme.grey),
        text: {
            primary: theme.text.primary,
            secondary: theme.text.secondary,
            disabled: theme.text.disabled,
            contrastText: theme.text.contrast,
        },
        divider: theme.components.divider.default.borderColor,
        background: {
            main: theme.background.main,
            paper: theme.background.paper,
        },
        action: {
            active: theme.action.default.enabled.backgroundColor,
            hover: theme.action.default.hovered.backgroundColor,
            selected: theme.action.default.selected.backgroundColor,
            disabled: theme.action.default.disabled.backgroundColor,
        },
    };
};

const nextMaterialUITheme = (theme, textAlignment) => {
    return createTheme({
        breakpoints,
        direction: textAlignment,
        typography: typography(theme),
        palette: palette(theme),
        uxfabric: theme,
    });
};

const buildUrl = (fontFamily, format, url) => `url("${url}/${fontFamily}.${format}") format('${format}')`;
const buildFontLinkUrl = (fontFamily, format, url) => `${url}/${fontFamily}.${format}`;
const containerFontWrapper = (theme, containerId) => {
    const normalizedContainerId = containerId.startsWith("#") ? containerId : `#${containerId}`;
    const fontFamily = theme.global.body1.fontFamily.toLowerCase();
    return `
${normalizedContainerId} {
    font-family: "${fontFamily}", ${FALLBACK_FONT_FAMILY};
}`;
};
const processFonts = (fonts, func) => {
    Object.values(fonts).forEach(({ fontFamily, fontWeight }) => {
        func(fontFamily, fontWeight);
    });
};
const generateFontElements = (theme, blobStoragePath, containerId, elementGenerator) => {
    const fontElements = [];
    const uniqueFontFamilies = new Set();
    const addFontElement = (fontFamily, fontWeight) => {
        if (!fontFamily || uniqueFontFamilies.has(fontFamily))
            return;
        uniqueFontFamilies.add(fontFamily);
        fontElements.push(elementGenerator(fontFamily, fontWeight, blobStoragePath));
    };
    processFonts(theme.global, addFontElement);
    processFonts(theme.component, addFontElement);
    const fontStyles = fontElements.join("\n");
    const containerStyles = containerId ? containerFontWrapper(theme, containerId) : "";
    return { fontStyles, containerStyles };
};
const FONTS = (theme, blobStoragePath, containerId) => {
    const { fontStyles, containerStyles } = generateFontElements(theme, blobStoragePath, containerId, (fontFamily, fontWeight, fontPath) => `
@font-face {
    font-family: "${fontFamily}";
    src: ${buildUrl(fontFamily, SUPPORTED_FONT_FORMAT, fontPath)};
    font-weight: ${fontWeight};
}`.trim());
    return `
<style id="font-ssr-styles-${containerId}" type="text/css">
${fontStyles}
${containerStyles}
</style>`.trim();
};
const FONT_LINKS = (theme, blobStoragePath, containerId, preloadFonts = true) => {
    const { fontStyles } = generateFontElements(theme, blobStoragePath, containerId, (fontFamily, _fontWeight, fontPath) => `<link ${preloadFonts ? `rel="preload"` : ""} href="${buildFontLinkUrl(fontFamily, SUPPORTED_FONT_FORMAT, fontPath)}" as="font" type="font/${SUPPORTED_FONT_FORMAT}" crossorigin>`.trim());
    return `${fontStyles}`.trim();
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var name = "@next/ux-fabric";
var version = "3.2.0-alpha01";

const getRealmName = (configurationKeys, req, res) => {
    var _a, _b;
    return req.headers["x-next-realm"] || ((_b = (_a = res.locals.configuration) === null || _a === void 0 ? void 0 : _a[configurationKeys.realm]) === null || _b === void 0 ? void 0 : _b.Value) || "";
};
const getThemeVariant = (configurationKeys, res) => { var _a, _b; return ((_b = (_a = res.locals.configuration) === null || _a === void 0 ? void 0 : _a[configurationKeys.variant]) === null || _b === void 0 ? void 0 : _b.Value) || ""; };
const getThemeUrl = (themePath, themeVersion, themeName) => {
    return `${themePath}/${THEME_FOLDER}/${themeVersion}/${THEME_FILE_NAME_PREFIX}${themeName.toLowerCase()}.json`;
};
const getTheme = (cache, themeUrl, ttl) => __awaiter(void 0, void 0, void 0, function* () {
    let theme = cache.get(themeUrl);
    if (!theme) {
        try {
            const timestamp = Date.now();
            const { data } = yield axios.get(themeUrl);
            logger.warn(`Theme loaded from CDN - url: ${themeUrl} - in ${Date.now() - timestamp}ms`);
            theme = data;
            cache.set(themeUrl, theme, ttl);
        }
        catch (err) {
            logger.error(`Error getting theme ${themeUrl}.`);
        }
    }
    return theme;
});
const themeMiddleware = (themePath, configurationKeys, cache, featureConfiguration) => (req, res, nextOp) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    try {
        const themeVersion = version ? `v${version.split(".")[0]}` : undefined;
        const ttl = (_c = (_b = (_a = res.locals.configuration) === null || _a === void 0 ? void 0 : _a[configurationKeys.appCache]) === null || _b === void 0 ? void 0 : _b.Value) !== null && _c !== void 0 ? _c : 0;
        let themeName = (featureConfiguration && ((_d = res.locals.featureSettings) === null || _d === void 0 ? void 0 : _d[featureConfiguration.themeName])) || "";
        let realm = getRealmName(configurationKeys, req, res);
        if (!realm) {
            realm = FALLBACK_REALM;
            logger.warn(`Error getting realm, defaulting to ${FALLBACK_REALM}`);
        }
        if (!themeVersion) {
            const errMsg = `Error getting ${version} theme version from ${name}/package.json file`;
            throw new Error(errMsg);
        }
        if (!themeName) {
            let themeVariant = getThemeVariant(configurationKeys, res);
            if (!themeVariant) {
                themeVariant = DEFAULT_VARIANT;
                logger.warn("No variant found in configuration, not using variant");
            }
            const variant = themeVariant.toLowerCase() === DEFAULT_VARIANT ? "" : `_${themeVariant.toLowerCase()}`;
            themeName = `${realm}${variant}`;
            req.themeVariant = themeVariant;
            logger.warn(`Error getting theme name, defaulting to ${themeName}`);
        }
        let theme = null;
        let themeUrl = getThemeUrl(themePath, themeVersion, themeName);
        theme = yield getTheme(cache, themeUrl, ttl);
        if (!theme) {
            themeUrl = getThemeUrl(themePath, themeVersion, realm);
            logger.warn(`Error getting theme with name: ${themeName}, defaulting to realm: ${realm}`);
            theme = yield getTheme(cache, themeUrl, ttl);
        }
        req.theme = theme;
        req.themeVersion = themeVersion;
    }
    catch (err) {
        logger.error(err);
    }
    nextOp();
});

class ThemeModel {
    constructor() {
        this.primary = {
            main: "",
            dark: "",
            light: "",
            contrast: "",
            shades: {
                "4p": "",
                "8p": "",
                "12p": "",
                disabled30p: "",
                "50p": "",
            },
        };
        this.secondary = {
            main: "",
            dark: "",
            light: "",
            contrast: "",
            shades: {
                "4p": "",
                "8p": "",
                "12p": "",
                disabled30p: "",
                "50p": "",
            },
        };
        this.text = {
            primary: "",
            secondary: "",
            disabled: "",
            contrast: "",
        };
        this.action = {
            default: {
                enabled: {
                    backgroundColor: "",
                    borderColor: "",
                    iconColor: "",
                },
                hovered: {
                    backgroundColor: "",
                    borderColor: "",
                },
                selected: {
                    backgroundColor: "",
                    borderColor: "",
                },
                disabled: {
                    backgroundColor: "",
                    borderColor: "",
                    iconColor: "",
                },
                error: {
                    backgroundColor: "",
                    borderColor: "",
                    iconColor: "",
                },
            },
            strong: {
                enabled: {
                    backgroundColor: "",
                    borderColor: "",
                    borderBottomColor: "",
                },
                hovered: {
                    backgroundColor: "",
                    borderColor: "",
                    borderBottomColor: "",
                },
                selected: {
                    backgroundColor: "",
                    borderColor: "",
                    borderBottomColor: "",
                },
                disabled: {
                    backgroundColor: "",
                    borderColor: "",
                    borderBottomColor: "",
                },
                error: {
                    backgroundColor: "",
                    borderColor: "",
                    iconColor: "",
                    borderBottomColor: "",
                },
            },
        };
        this.grey = {
            50: "",
            100: "",
            200: "",
            300: "",
            400: "",
            500: "",
            600: "",
            700: "",
            800: "",
            900: "",
            white: "",
            black: "",
        };
        this.background = {
            main: "",
            paper: "",
        };
        this.interactive = {
            focus: {
                default: {
                    outlineColor: "",
                },
            },
        };
        this.brand = {
            clearance: {
                default: {
                    backgroundColor: "",
                    contrastTextColor: "",
                    textColor: "",
                },
            },
            markdown: {
                default: {
                    backgroundColor: "",
                    contrastTextColor: "",
                    textColor: "",
                },
            },
            promotion: {
                default: {
                    backgroundColor: "",
                    contrastTextColor: "",
                    textColor: "",
                },
            },
            sale: {
                default: {
                    backgroundColor: "",
                    contrastTextColor: "",
                    textColor: "",
                },
            },
            error: {
                default: {
                    textColor: "",
                },
                stock: {
                    textColor: "",
                },
            },
            info: {
                default: {
                    textColor: "",
                },
                stock: {
                    textColor: "",
                },
            },
            success: {
                default: {
                    textColor: "",
                },
                stock: {
                    textColor: "",
                },
            },
            warning: {
                default: {
                    textColor: "",
                },
                stock: {
                    textColor: "",
                },
            },
        };
        this.components = {
            alert: {
                error: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                        iconColor: "",
                        textColor: "",
                    },
                },
                warning: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                        iconColor: "",
                        textColor: "",
                    },
                },
                info: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                        iconColor: "",
                        textColor: "",
                    },
                },
                success: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                        iconColor: "",
                        textColor: "",
                    },
                },
            },
            appBar: {
                globalBar: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                        iconColor: "",
                        textColor: "",
                        divider: {
                            default: {
                                borderColor: "",
                            },
                        },
                        button: {
                            outlined: {
                                primary: {
                                    enabled: {
                                        borderColor: "",
                                    },
                                },
                            },
                        },
                    },
                },
                brandBar: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                        iconColor: "",
                        textColor: "",
                        tab: {
                            default: {
                                active: {
                                    borderColor: "",
                                },
                                enabled: {
                                    borderColor: "",
                                },
                            },
                        },
                        button: {
                            outlined: {
                                primary: {
                                    enabled: {
                                        borderColor: "",
                                    },
                                },
                            },
                        },
                    },
                    secure: {
                        borderColor: "",
                    },
                },
                navBar: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                        iconColor: "",
                        gradientLeft: "",
                        gradientRight: "",
                    },
                },
                backToNext: {
                    default: {
                        backgroundColor: "",
                        textColor: "",
                        iconColor: "",
                    },
                },
            },
            avatar: {
                root: {
                    borderColor: "",
                },
                iconAvatar: {
                    backgroundColor: "",
                    iconColor: "",
                },
                textAvatar: {
                    backgroundColor: "",
                    textColor: "",
                },
            },
            backdrop: {
                default: {
                    backgroundColor: "",
                },
                paper: {
                    backgroundColor: "",
                },
            },
            badge: {
                primary: {
                    backgroundColor: "",
                    iconColor: "",
                    textColor: "",
                },
                secondary: {
                    backgroundColor: "",
                    iconColor: "",
                    textColor: "",
                },
                notification: {
                    backgroundColor: "",
                    iconColor: "",
                    textColor: "",
                },
            },
            breadcrumb: {
                default: {
                    backgroundColor: "",
                    borderBottomColor: "",
                    borderTopColor: "",
                    gradientLeft: "",
                    gradientRight: "",
                    button: {
                        enabled: {
                            backgroundColor: "",
                            borderTopColor: "",
                            borderBottomColor: "",
                            borderLeftColor: "",
                            borderRightColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            borderTopColor: "",
                            borderBottomColor: "",
                            borderLeftColor: "",
                            borderRightColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                    },
                },
            },
            button: {
                contained: {
                    primary: {
                        enabled: {
                            contrastTextColor: "",
                            iconColor: "",
                            backgroundColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        disabled: {
                            backgroundColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                    },
                    secondary: {
                        enabled: {
                            backgroundColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        disabled: {
                            backgroundColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                    },
                },
                outlined: {
                    primary: {
                        enabled: {
                            backgroundColor: "",
                            borderColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            borderColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        disabled: {
                            backgroundColor: "",
                            borderColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                    },
                    secondary: {
                        enabled: {
                            backgroundColor: "",
                            borderColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            borderColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        disabled: {
                            backgroundColor: "",
                            borderColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                    },
                },
                unstyled: {
                    primary: {
                        enabled: {
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        hovered: {
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        disabled: {
                            contrastTextColor: "",
                            iconColor: "",
                        },
                    },
                    secondary: {
                        enabled: {
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        hovered: {
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        disabled: {
                            contrastTextColor: "",
                            iconColor: "",
                        },
                    },
                },
            },
            buttonGroup: {
                default: {
                    borderBottomColor: "",
                    borderTopColor: "",
                    borderColor: "",
                },
            },
            card: {
                cardHeader: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                        textColor: "",
                        iconColor: "",
                    },
                },
                cardActions: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                    },
                },
                cardContent: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                    },
                },
            },
            checkbox: {
                default: {
                    unselected: {
                        enabled: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                        disabled: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                    },
                    selected: {
                        enabled: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                        disabled: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                    },
                    checkmark: {
                        enabled: {
                            iconColor: "",
                        },
                        disabled: {
                            iconColor: "",
                        },
                    },
                },
            },
            chip: {
                filled: {
                    default: {
                        enabled: {
                            backgroundColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        disabled: {
                            backgroundColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                    },
                },
                outlined: {
                    default: {
                        enabled: {
                            backgroundColor: "",
                            borderColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            borderColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                        disabled: {
                            backgroundColor: "",
                            borderColor: "",
                            contrastTextColor: "",
                            iconColor: "",
                        },
                    },
                },
            },
            dialog: {
                dialogHeader: {
                    default: {
                        backgroundColor: "",
                        borderBottomColor: "",
                        borderColor: "",
                        textColor: "",
                        iconColor: "",
                    },
                },
                dialogContent: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                        textColor: "",
                    },
                },
                dialogActions: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                        borderTopColor: "",
                        textColor: "",
                    },
                },
            },
            divider: {
                default: {
                    borderColor: "",
                },
                strong: {
                    borderColor: "",
                },
            },
            drawer: {
                default: {
                    iconColor: "",
                },
            },
            floatingActionButton: {
                default: {
                    enabled: {
                        backgroundColor: "",
                        iconColor: "",
                    },
                    focused: {
                        backgroundColor: "",
                        iconColor: "",
                    },
                    hovered: {
                        backgroundColor: "",
                        iconColor: "",
                    },
                },
            },
            footer: {
                default: {
                    backgroundColor: "",
                    action: {
                        default: {
                            enabled: {
                                iconColor: "",
                            },
                        },
                    },
                    link: {
                        primary: {
                            enabled: {
                                textColor: "",
                            },
                            hovered: {
                                textColor: "",
                            },
                        },
                        inherit: {
                            enabled: {
                                textColor: "",
                            },
                            hovered: {
                                textColor: "",
                            },
                        },
                    },
                    text: {
                        primary: "",
                        secondary: "",
                    },
                    divider: {
                        default: {
                            borderColor: "",
                        },
                    },
                },
            },
            imageCarousel: {
                default: {
                    button: {
                        contained: {
                            addToBag: {
                                enabled: {
                                    backgroundColor: "",
                                    textColor: "",
                                    iconColor: "",
                                },
                                hovered: {
                                    backgroundColor: "",
                                    textColor: "",
                                    iconColor: "",
                                },
                                disabled: {
                                    backgroundColor: "",
                                    textColor: "",
                                    iconColor: "",
                                },
                            },
                        },
                        outlined: {
                            enlarge: {
                                enabled: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                                hovered: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                                disabled: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                            },
                            favourite: {
                                enabled: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                                hovered: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                                disabled: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                            },
                            navigation: {
                                enabled: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                                hovered: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                                disabled: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                            },
                            video: {
                                enabled: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                                hovered: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                                disabled: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    iconColor: "",
                                },
                            },
                        },
                    },
                },
            },
            link: {
                primary: {
                    enabled: {
                        textColor: "",
                    },
                    hovered: {
                        textColor: "",
                    },
                },
                inherit: {
                    enabled: {
                        textColor: "",
                    },
                    hovered: {
                        textColor: "",
                    },
                },
                contrast: {
                    enabled: {
                        textColor: "",
                    },
                    hovered: {
                        textColor: "",
                    },
                },
            },
            menu: {
                default: {
                    root: {
                        backgroundColor: "",
                        borderColor: "",
                    },
                    enabled: {
                        textColor: "",
                    },
                    disabled: {
                        textColor: "",
                    },
                    hovered: {
                        backgroundColor: "",
                        textColor: "",
                    },
                    selected: {
                        backgroundColor: "",
                        textColor: "",
                    },
                },
                megaMenu: {
                    default: {
                        backgroundColor: "",
                        textColor: "",
                    },
                    mission: {
                        backgroundColor: "",
                        textColor: "",
                    },
                    gutter: {
                        backgroundColor: "",
                    },
                    navigationList: {
                        default: {
                            divider: {
                                default: {
                                    borderColor: "",
                                },
                            },
                        },
                        quickLinks: {
                            backgroundColor: "",
                            divider: {
                                default: {
                                    borderColor: "",
                                },
                            },
                        },
                    },
                },
                searchMenu: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                    },
                    enriched: {
                        backgroundColor: "",
                    },
                },
            },
            pagination: {
                imageCarousel: {
                    root: {
                        backgroundColor: "",
                        borderColor: "",
                    },
                    enabled: {
                        backgroundColor: "",
                        borderColor: "",
                        iconColor: "",
                    },
                    active: {
                        borderColor: "",
                        backgroundColor: "",
                        iconColor: "",
                    },
                },
                thumbnailCarousel: {
                    root: {
                        backgroundColor: "",
                        borderColor: "",
                    },
                    enabled: {
                        borderColor: "",
                    },
                    hovered: {
                        borderColor: "",
                    },
                    active: {
                        borderColor: "",
                    },
                },
            },
            pill: {
                default: {
                    active: {
                        backgroundColor: "",
                        borderColor: "",
                        textColor: "",
                    },
                    disabled: {
                        backgroundColor: "",
                        borderColor: "",
                        highlightColor1: "",
                        highlightColor2: "",
                        textColor: "",
                    },
                    disabledActive: {
                        backgroundColor: "",
                        borderColor: "",
                        highlightColor1: "",
                        highlightColor2: "",
                        textColor: "",
                    },
                    enabled: {
                        backgroundColor: "",
                        borderColor: "",
                        textColor: "",
                    },
                    hovered: {
                        backgroundColor: "",
                        borderColor: "",
                        textColor: "",
                    },
                },
                image: {
                    active: {
                        backgroundColor: "",
                        borderColor: "",
                        textColor: "",
                    },
                    disabled: {
                        backgroundColor: "",
                        borderColor: "",
                        highlightColor1: "",
                        highlightColor2: "",
                        textColor: "",
                    },
                    disabledActive: {
                        backgroundColor: "",
                        borderColor: "",
                        highlightColor1: "",
                        highlightColor2: "",
                        textColor: "",
                    },
                    enabled: {
                        backgroundColor: "",
                        borderColor: "",
                        textColor: "",
                    },
                    hovered: {
                        backgroundColor: "",
                        borderColor: "",
                        textColor: "",
                    },
                },
            },
            progress: {
                circular: {
                    default: {
                        highlightColor: "",
                    },
                },
                linear: {
                    reviews: {
                        root: {
                            backgroundColor: "",
                        },
                        enabled: {
                            backgroundColor: "",
                            highlightColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            highlightColor: "",
                        },
                        disabled: {
                            backgroundColor: "",
                        },
                    },
                },
            },
            radio: {
                default: {
                    checkmark: {
                        disabled: {
                            iconColor: "",
                        },
                        enabled: {
                            iconColor: "",
                        },
                    },
                    selected: {
                        disabled: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                        enabled: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            borderColor: ")",
                        },
                    },
                    unselected: {
                        disabled: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                        enabled: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                        hovered: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                    },
                },
            },
            rating: {
                default: {
                    enabled: {
                        backgroundColor: "",
                    },
                    hovered: {
                        backgroundColor: "",
                    },
                    disabled: {
                        backgroundColor: "",
                    },
                },
            },
            ribbon: {
                default: {
                    root: {
                        button: {
                            outlined: {
                                navigation: {
                                    hovered: {
                                        backgroundColor: "",
                                        borderColor: "",
                                        iconColor: "",
                                    },
                                    enabled: {
                                        iconColor: "",
                                        backgroundColor: "",
                                        borderColor: "",
                                    },
                                },
                            },
                        },
                    },
                    enabled: {
                        borderColor: "",
                    },
                },
                recentlyViewed: {
                    root: {
                        borderColor: "",
                        chip: {
                            outlined: {
                                timestamp: {
                                    enabled: {
                                        borderColor: "",
                                        backgroundColor: "",
                                        textColor: "",
                                    },
                                },
                            },
                        },
                    },
                    enabled: {
                        borderColor: "",
                        chip: {
                            outlined: {
                                bag: {
                                    enabled: {
                                        backgroundColor: "",
                                        borderColor: "",
                                        textColor: "",
                                    },
                                },
                            },
                        },
                    },
                },
            },
            searchBanner: {
                default: {
                    banner: {
                        root: {
                            backgroundColor: "",
                        },
                        copyText: {
                            textColor: "",
                            link: {
                                primary: {
                                    enabled: {
                                        contrastTextColor: "",
                                    },
                                    hovered: {
                                        contrastTextColor: "",
                                    },
                                },
                            },
                        },
                        chips: {
                            outlined: {
                                enabled: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    contrastTextColor: "",
                                },
                                hovered: {
                                    backgroundColor: "",
                                    borderColor: "",
                                    contrastTextColor: "",
                                },
                            },
                        },
                        imageChips: {
                            backgroundColor: "",
                            textColor: "",
                            enabled: {
                                borderColor: "",
                            },
                            hovered: {
                                borderColor: "",
                            },
                        },
                    },
                    stripBanner: {
                        backgroundColor: "",
                        borderColor: "",
                        button: {
                            backgroundColor: "",
                            borderColor: "",
                            contrastTextColor: "",
                        },
                        typography: {
                            textColor: "",
                        },
                    },
                },
            },
            skeleton: {
                default: {
                    backgroundColor: "",
                },
            },
            tab: {
                default: {
                    root: {
                        borderColor: "",
                    },
                    enabled: {
                        backgroundColor: "",
                        borderColor: "",
                        borderBottomColor: "",
                        textColor: "",
                    },
                    hovered: {
                        backgroundColor: "",
                        borderColor: "",
                        borderBottomColor: "",
                        textColor: "",
                    },
                    active: {
                        backgroundColor: "",
                        borderColor: "",
                        borderBottomColor: "",
                        highlightColor: "",
                        textColor: "",
                    },
                    disabled: {
                        backgroundColor: "",
                        borderColor: "",
                        highlightColor: "",
                        borderBottomColor: "",
                        textColor: "",
                    },
                },
                nav: {
                    default: {
                        enabled: {
                            backgroundColor: "",
                            textColor: "",
                        },
                        active: {
                            backgroundColor: "",
                            textColor: "",
                            highlightColor: "",
                        },
                    },
                    sale: {
                        enabled: {
                            textColor: "",
                        },
                        active: {
                            textColor: "",
                        },
                    },
                    clearance: {
                        enabled: {
                            textColor: "",
                        },
                        active: {
                            textColor: "",
                        },
                    },
                },
            },
            table: {
                default: {
                    enabled: {
                        root: {
                            backgroundColor: "",
                            borderColor: "",
                        },
                        cellHeader: {
                            backgroundColor: "",
                            borderBottomColor: "",
                            borderColor: "",
                            textColor: "",
                        },
                        cellBodyEven: {
                            backgroundColor: "",
                            borderBottomColor: "",
                            borderColor: "",
                            textColor: "",
                        },
                        cellBodyOdd: {
                            backgroundColor: "",
                            borderBottomColor: "",
                            borderColor: "",
                            textColor: "",
                        },
                    },
                },
            },
            toggleButton: {
                selected: {
                    default: {
                        backgroundColor: "",
                        borderColor: "",
                    },
                },
            },
            tooltip: {
                default: {
                    backgroundColor: "",
                    borderColor: "",
                    textColor: "",
                },
            },
        };
        this.elevation = {
            menu: "",
            floatingActionButton: "",
            popover: "",
            tooltip: "",
            header: "",
        };
        this.global = {
            h1: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            h2: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            h3: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            h4: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            h5: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            h6: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            body1: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            body2: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            body3: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            body4: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            body5: {
                fontFamily: "",
                fontWeight: 700,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            body6: {
                fontFamily: "",
                fontWeight: 700,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            subtitle1: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            subtitle2: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            overline: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 1,
                lineHeight: 1.5,
            },
            caption: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
        };
        this.component = {
            buttonLarge: {
                fontFamily: "",
                fontWeight: 600,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            buttonMedium: {
                fontFamily: "",
                fontWeight: 600,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            buttonSmall: {
                fontFamily: "",
                fontWeight: 600,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            inputLabel1: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            inputLabel2: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            inputText: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            menuItem: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            menuItemDense: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            pillLabel1: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            pillLabel2: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            tabText1: {
                fontFamily: "",
                fontWeight: 600,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            tabText2: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            tabTextNav: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 0.18,
            },
            chip: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            chipLabel2: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            helperText: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            badgeLabel: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            tooltip: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            listItemSubtitle1: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            listItemSubtitle2: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            listItemDescription: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
            avatarInitials: {
                fontFamily: "",
                fontWeight: 400,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 0.2,
            },
            searchListItemLabel: {
                fontFamily: "",
                fontWeight: 500,
                textTransform: "",
                textDecoration: "",
                fontSize: "",
                letterSpacing: 0,
                lineHeight: 1.5,
            },
        };
        this.shape = {
            global: {
                button: {
                    small: {
                        borderRadius: "",
                    },
                    medium: {
                        borderRadius: "",
                    },
                    large: {
                        borderRadius: "",
                    },
                },
                iconButton: {
                    medium: {
                        borderRadius: "",
                    },
                    large: {
                        borderRadius: "",
                    },
                },
                select: {
                    medium: {
                        borderRadius: "",
                    },
                    large: {
                        borderRadius: "",
                    },
                },
                menu: {
                    default: {
                        borderRadius: "",
                    },
                },
                textField: {
                    medium: {
                        borderRadius: "",
                    },
                    large: {
                        borderRadius: "",
                    },
                },
                toggleButton: {
                    medium: {
                        borderRadius: "",
                    },
                    large: {
                        borderRadius: "",
                    },
                },
                fitPill: {
                    medium: {
                        borderRadius: "",
                    },
                    large: {
                        borderRadius: "",
                    },
                },
                colorPill: {
                    medium: {
                        borderRadius: "",
                    },
                    large: {
                        borderRadius: "",
                    },
                },
                sizePill: {
                    medium: {
                        borderRadius: "",
                    },
                    large: {
                        borderRadius: "",
                    },
                },
                floatingActionButton: {
                    default: {
                        borderRadius: "",
                    },
                },
                tooltip: {
                    default: {
                        borderRadius: "",
                    },
                },
                popover: {
                    default: {
                        borderRadius: "",
                    },
                },
                check: {
                    default: {
                        borderRadius: "",
                    },
                },
                chip: {
                    small: {
                        borderRadius: "",
                    },
                    medium: {
                        borderRadius: "",
                    },
                },
                imageChip: {
                    default: {
                        borderRadius: "",
                    },
                },
                ribbonButton: {
                    default: {
                        borderRadius: "",
                    },
                },
            },
        };
    }
}

const formatKey = (oldKey, newKey) => {
    return oldKey ? `${oldKey} - ${newKey}` : newKey;
};
const validateSettings = (theme, themeToTest, parentKey) => {
    return Object.keys(theme).some(key => {
        const combinedKey = formatKey(parentKey, key);
        const isValidNumberZero = typeof theme[key] === "number" && themeToTest[key] === 0;
        if (!themeToTest[key] && !isValidNumberZero) {
            logger.error(`Theme: ${combinedKey} is not provided`);
            return true;
        }
        if (typeof theme[key] === "object") {
            return validateSettings(theme[key], themeToTest[key], combinedKey) > 0;
        }
        return false;
    });
};
const isThemeValid = (theme) => {
    if (!theme) {
        logger.error("Theme is not provided");
        return false;
    }
    return !validateSettings(new ThemeModel(), theme, "");
};

const NextThemeProvider = ({ uxfabric, textAlignment = DEFAULT_TEXT_DIRECTION, children }) => (React.createElement(ThemeProvider, { theme: nextMaterialUITheme(uxfabric, textAlignment) }, children));

export { FALLBACK_FONT_FAMILY, FONTS, FONT_LINKS, NextThemeProvider, ThemeModel, breakpoints, buildFontLinkUrl, buildUrl, containerFontWrapper, isThemeValid, mockTheme, mockUxFabric, nextMaterialUITheme, themeMiddleware };
